import styled, { keyframes, css } from 'styled-components';
import backgroundImage from '../assets/images/background.webp';
import portadaImage from '../assets/recursos/portada-square.webp';

// ========================================
// Animations
// ========================================

export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

export const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 8px 25px rgba(217, 125, 43, 0.4),
                0 0 0 0 rgba(217, 125, 43, 0.7),
                0 0 0 0 rgba(217, 125, 43, 0.4);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 12px 40px rgba(217, 125, 43, 0.6),
                0 0 0 10px rgba(217, 125, 43, 0),
                0 0 0 20px rgba(217, 125, 43, 0);
  }
`;

export const bounce = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-15px) scale(1.08);
  }
`;

export const glow = keyframes`
  0%, 100% {
    box-shadow: 0 8px 25px rgba(217, 125, 43, 0.4),
                0 0 20px rgba(217, 125, 43, 0.3),
                0 0 40px rgba(217, 125, 43, 0.2);
  }
  50% {
    box-shadow: 0 12px 40px rgba(217, 125, 43, 0.7),
                0 0 30px rgba(217, 125, 43, 0.5),
                0 0 60px rgba(217, 125, 43, 0.3);
  }
`;

export const float3D = keyframes`
  0% {
    transform: translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg) scale(1);
    box-shadow: 0 8px 25px rgba(217, 125, 43, 0.4);
  }
  25% {
    transform: translate3d(2px, -8px, 0) rotateX(2deg) rotateY(-3deg) scale(1.03);
    box-shadow: 0 10px 30px rgba(217, 125, 43, 0.45);
  }
  50% {
    transform: translate3d(0, -12px, 0) rotateX(0deg) rotateY(0deg) scale(1.05);
    box-shadow: 0 12px 35px rgba(217, 125, 43, 0.5);
  }
  75% {
    transform: translate3d(-2px, -8px, 0) rotateX(-2deg) rotateY(3deg) scale(1.03);
    box-shadow: 0 10px 30px rgba(217, 125, 43, 0.45);
  }
  100% {
    transform: translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg) scale(1);
    box-shadow: 0 8px 25px rgba(217, 125, 43, 0.4);
  }
`;

export const auraPulse = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(217, 125, 43, 0.4),
                0 0 0 0 rgba(217, 125, 43, 0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
    box-shadow: 0 0 0 10px rgba(217, 125, 43, 0),
                0 0 0 20px rgba(217, 125, 43, 0);
  }
`;

export const particleFloat = keyframes`
  0% {
    transform: translate3d(0, 0, 0) scale(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  100% {
    transform: translate3d(var(--tx), var(--ty), 0) scale(1);
    opacity: 0;
  }
`;

export const rotateSlow = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

export const ripple = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

export const ripple2 = keyframes`
  0% {
    transform: scale(0.9);
    opacity: 0.8;
  }
  100% {
    transform: scale(2.8);
    opacity: 0;
  }
`;

export const shine = keyframes`
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
`;

export const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const slideOutDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
`;

// ========================================
// Main Container
// ========================================

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f5f5f5;
`;

// ========================================
// Hero Section
// ========================================

export const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(30, 76, 92, 0.85) 0%,
      rgba(217, 125, 43, 0.75) 100%
    );
    z-index: 1;
  }
`;

export const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transform: translate3d(0, ${props => props.$offset * 0.5}px, 0);
  will-change: transform;
  
  @media (max-width: 768px) {
    transform: none;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 2rem;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  animation: ${fadeInUp} 1s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const HeroTitle = styled.h1`
  color: white;
  font-size: clamp(2.5rem, 8vw, 5rem);
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-shadow: 2px 4px 12px rgba(0, 0, 0, 0.3);
  letter-spacing: -1px;
  line-height: 1.1;
`;

export const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: clamp(1.1rem, 3vw, 1.5rem);
  margin-bottom: 3rem;
  text-shadow: 1px 2px 8px rgba(0, 0, 0, 0.2);
  font-weight: 300;
  line-height: 1.6;
`;

export const CTAButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem 3rem;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(217, 125, 43, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(217, 125, 43, 0.6);
    background: linear-gradient(135deg, #F5A64A 0%, #D97D2B 100%);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

export const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 49%;
  transform: translateX(-49%);
  z-index: 2;
  animation: ${float} 2s ease-in-out infinite;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ScrollArrow = styled.div`
  width: 30px;
  height: 30px;
  border-left: 3px solid white;
  border-bottom: 3px solid white;
  transform: rotate(-45deg);
  opacity: 0.8;
`;

// ========================================
// Content Sections
// ========================================

export const Section = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.$background || '#f5f5f5'};
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

export const ResourcesSectionWrapper = styled(Section)`
  background: url(${portadaImage}) center center;
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  
  /* Add semi-transparent overlay for better readability */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.6);
    z-index: 0;
  }
  
  /* Ensure content is above the overlay */
  & > * {
    position: relative;
    z-index: 1;
  }
  
  /* Mobile optimizations - disable fixed attachment and improve background */
  @media (max-width: 768px) {
    background-attachment: scroll;
    background: linear-gradient(
      135deg,
      rgba(30, 76, 92, 0.15) 0%,
      rgba(217, 125, 43, 0.1) 50%,
      rgba(30, 76, 92, 0.15) 100%
    ),
    url(${portadaImage}) center center;
    background-size: cover;
    background-position: center;
    
    /* Enhanced overlay for mobile with gradient */
    &::before {
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.75) 0%,
        rgba(255, 255, 255, 0.65) 50%,
        rgba(255, 255, 255, 0.75) 100%
      );
    }
  }
  
  /* Extra small devices - even more subtle background */
  @media (max-width: 480px) {
    background: linear-gradient(
      135deg,
      rgba(30, 76, 92, 0.2) 0%,
      rgba(217, 125, 43, 0.15) 50%,
      rgba(30, 76, 92, 0.2) 100%
    ),
    url(${portadaImage}) center center;
    background-size: cover;
    background-position: center;
    
    &::before {
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.8) 0%,
        rgba(255, 255, 255, 0.7) 50%,
        rgba(255, 255, 255, 0.8) 100%
      );
    }
  }
`;

export const LinksSectionWrapper = styled(Section)`
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%);
`;

export const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(2rem, 5vw, 3rem);
  color: #1E4C5C;
  margin-bottom: 1rem;
  font-weight: 700;
`;

export const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  color: #666;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

// ========================================
// Games Section
// ========================================

export const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;
  justify-items: center;
  align-items: stretch;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const GameCard = styled.div`
  position: relative;
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 550px;
  border: 3px solid transparent;
  background-clip: padding-box;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(217, 125, 43, 0.25);
  }

  @media (max-width: 768px) {
    max-width: 450px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    max-width: 100%;
  }
`;

export const GameImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 210px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$bgColor || 'transparent'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    ${props => props.$bgImage ? css`
      background-image: url(${props.$bgImage});
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    ` : css`
      background: radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%);
    `}
    transition: transform 0.6s ease;
    z-index: 0;
  }

  ${GameCard}:hover &::before {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    height: 180px;
  }
`;

export const GameImage = styled.img`
  max-width: 65%;
  max-height: 65%;
  object-fit: contain;
  transition: transform 0.5s ease;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.15));
  position: relative;
  z-index: 1;
  
  ${GameCard}:hover & {
    transform: scale(1.1) rotate(2deg);
  }
`;

export const StatusBadge = styled.span`
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.35rem 0.85rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(4px);

  ${props => {
    switch (props.$status) {
      case 'available':
        return css`
          background: linear-gradient(135deg, #1E4C5C 0%, #255E72 100%);
          color: #ffffff;
        `;
      case 'preSale':
        return css`
          background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
          color: #ffffff;
        `;
      case 'comingSoon':
      default:
        return css`
          background: linear-gradient(135deg, #667eea 0%, #5a67d8 100%);
          color: #ffffff;
        `;
    }
  }}
`;

export const GameBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 44px;
  height: 44px;
  background: #D97D2B;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(217, 125, 43, 0.4);
  transition: all 0.3s ease;
  z-index: 10;
  
  svg {
    color: white;
    font-size: 1.3rem;
  }
  
  ${GameCard}:hover & {
    background: #C06A22;
    transform: scale(1.1) rotate(5deg);
    box-shadow: 0 6px 20px rgba(217, 125, 43, 0.5);
  }
  
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    top: 0.75rem;
    right: 0.75rem;
    
    svg {
      font-size: 1.1rem;
    }
  }
`;

export const GameInfo = styled.div`
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  z-index: 2;
  background: white;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

export const GameTitle = styled.h3`
  font-size: 1.6rem;
  color: #1E4C5C;
  margin-bottom: 0.4rem;
  font-weight: 800;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

export const GameSubtitle = styled.h4`
  font-size: 0.9rem;
  color: #D97D2B;
  font-weight: 600;
  margin-bottom: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const GameDescription = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.55;
  flex: 1;
  margin-bottom: 1.25rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

export const GameActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: auto;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

export const ActionButton = styled.a`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  text-align: center;
  transition: all 0.25s ease;
  cursor: pointer;
  border: 2px solid transparent;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  ${props => {
    if (props.disabled) {
      return css`
        background: #f0f0f0 !important;
        color: #aaaaaa !important;
        border-color: #e0e0e0 !important;
        box-shadow: none !important;
        cursor: not-allowed !important;
        opacity: 0.65;
        transform: none !important;
      `;
    }

    switch (props.$variant) {
      case 'primary':
        return css`
          background: linear-gradient(135deg, #1E4C5C 0%, #255E72 100%);
          color: white;
          border-color: transparent;
          &:hover {
            background: linear-gradient(135deg, #255E72 0%, #1E4C5C 100%);
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(30, 76, 92, 0.35);
          }
        `;
      case 'secondary':
        return css`
          background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
          color: white;
          border-color: transparent;
          &:hover {
            background: linear-gradient(135deg, #F5A64A 0%, #D97D2B 100%);
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(217, 125, 43, 0.35);
          }
        `;
      case 'outline':
      default:
        return css`
          background: transparent;
          color: #1E4C5C;
          border-color: #1E4C5C;
          &:hover {
            background: rgba(30, 76, 92, 0.06);
            transform: translateY(-2px);
          }
        `;
    }
  }}

  @media (max-width: 480px) {
    width: 100%;
  }
`;

// ========================================
// Floating Action Button
// ========================================

export const FloatingButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  border: 3px solid rgba(255, 255, 255, 0.4);
  cursor: pointer;
  display: none; /* Oculto por defecto (solo mobile) */
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow: visible;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  padding: 0;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
  
  /* Optimización de rendimiento */
  will-change: transform, box-shadow;
  
  /* Animación de entrada/salida */
  opacity: ${props => props.$isVisible ? 1 : 0};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  transform: ${props => props.$isVisible
    ? 'translate3d(0, 0, 0) scale(1)'
    : 'translate3d(0, 20px, 0) scale(0.8)'};
  
  /* Animaciones combinadas cuando está visible - efectos de juego */
  ${props => props.$isVisible
    ? css`
      animation: ${slideInUp} 0.5s ease-out,
                 ${float3D} 4s cubic-bezier(0.4, 0, 0.6, 1) infinite 0.5s;
    `
    : css`
      animation: ${slideOutDown} 0.4s ease-out;
    `}
  
  /* Aura pulsante alrededor del botón */
  &::after {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(217, 125, 43, 0.3) 0%, transparent 70%);
    ${props => props.$isVisible
    ? css`
        animation: ${auraPulse} 2s ease-in-out infinite;
      `
    : ''}
    pointer-events: none;
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.15) !important;
    box-shadow: 0 20px 50px rgba(217, 125, 43, 0.6) !important;
    background: linear-gradient(135deg, #F5A64A 0%, #D97D2B 100%);
    border-color: rgba(255, 255, 255, 0.7);
    animation-play-state: paused;
  }
  
  &:active {
    transform: translateY(-4px) scale(1.1) !important;
  }
  
  /* Solo mostrar en mobile */
  @media (max-width: 768px) {
    display: flex;
    width: 56px;
    height: 56px;
    bottom: 1.5rem;
    right: 1.5rem;
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    bottom: 1rem;
    right: 1rem;
  }
`;

export const FloatingButtonImage = styled.img`
  position: relative;
  z-index: 3;
  width: 140%;
  height: 140%;
  object-fit: contain;
  transition: transform 0.3s ease;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  
  ${FloatingButton}:hover & {
    transform: rotate(10deg) scale(1.2);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  }
`;

export const FloatingButtonRipple = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%);
  transform: translate(-50%, -50%) scale(0.8);
  animation: ${ripple} 2s ease-out infinite;
  pointer-events: none;
  z-index: 1;
`;

export const FloatingButtonRipple2 = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
  transform: translate(-50%, -50%) scale(0.9);
  animation: ${ripple2} 2.5s ease-out infinite 0.5s;
  pointer-events: none;
  z-index: 0;
`;

export const ParticleContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 200%;
  height: 200%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
`;

export const Particle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 4px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(217, 125, 43, 0.6) 100%);
  border-radius: 50%;
  --tx: ${props => Math.cos(props.$angle * Math.PI / 180) * 40}px;
  --ty: ${props => Math.sin(props.$angle * Math.PI / 180) * 40}px;
  animation: ${particleFloat} 2s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  box-shadow: 0 0 6px rgba(217, 125, 43, 0.8);
  will-change: transform, opacity;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

